import React, { useEffect } from "react";

export interface YhModalProps {
  open: boolean;
  onClose?: () => void;
  title?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  width?: string | number;
  closable?: boolean;
  fullScreen?: boolean;
  className?: string;
}

export const YhModal: React.FC<YhModalProps> = ({
  open,
  onClose,
  title,
  children,
  footer,
  width = 560,
  closable = true,
  fullScreen = false,
  className = "",
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, closable, onClose]);

  if (!open) return null;

  const widthStyle =
    typeof width === "number" ? `${width}px` : width || "60%";

  return (
    <div
      className="yh-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && closable) onClose?.();
      }}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        className={`yh-modal ${
          fullScreen ? "!max-h-none !h-screen !w-screen !rounded-none" : ""
        } ${className}`}
        style={fullScreen ? undefined : { maxWidth: widthStyle }}
      >
        {(title || closable) && (
          <div className="flex items-start justify-between gap-3 border-b border-yh-border px-5 py-4">
            <div className="min-w-0 flex-1 font-yh-bold text-lg text-[#1E1B4B]">
              {title}
            </div>
            {closable && (
              <button
                type="button"
                aria-label="Close"
                onClick={onClose}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-yh-md text-yh-black transition-colors hover:bg-yh-border-light hover:text-yh-blue"
              >
                ✕
              </button>
            )}
          </div>
        )}
        <div className="px-5 py-4">{children}</div>
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t border-yh-border px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default YhModal;
